import React, { useEffect, useState } from "react";
import LibraryFormModal from "../components/LibraryFormModal";
import LibraryField, {
  libraryInputClass,
  librarySelectClass,
} from "../components/LibraryField";
import { ChevronDownIcon } from "../../../Icons";
import { CARE_FACILITY_TYPES } from "../../../services/librariesCatalogService";

const EMPTY = {
  facilityName: "",
  facilityType: "Hospital",
  facilityCode: "",
  npiNumber: "",
  status: "Active",
};

const CareFacilityFormModal = ({ isOpen, record, onClose, onSave }) => {
  const isEdit = Boolean(record?.id);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setSaving(false);
    if (record) {
      setForm({
        facilityName: record.facilityName || "",
        facilityType: record.facilityType || "Hospital",
        facilityCode: record.facilityCode || "",
        npiNumber: record.npiNumber || "",
        status: record.status || "Active",
      });
    } else {
      setForm(EMPTY);
    }
  }, [isOpen, record]);

  const setField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.facilityName.trim()) {
      next.facilityName = "Facility name is required";
    }
    if (!form.facilityType.trim()) {
      next.facilityType = "Facility type is required";
    }
    const npi = form.npiNumber.trim();
    if (npi && !/^\d{10}$/.test(npi)) {
      next.npiNumber = "NPI must be exactly 10 digits";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await onSave?.({
        facilityName: form.facilityName.trim(),
        facilityType: form.facilityType.trim(),
        facilityCode: form.facilityCode.trim(),
        npiNumber: form.npiNumber.trim(),
        status: form.status,
      });
      onClose?.();
    } catch (err) {
      setErrors({ form: err.message || "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <LibraryFormModal
      isOpen={isOpen}
      title={isEdit ? "Edit Care Facility" : "Add Care Facility"}
      onClose={onClose}
      onSubmit={handleSubmit}
      isSaving={saving}
      submitLabel={isEdit ? "Update" : "Create"}
    >
      {errors.form ? (
        <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
          {errors.form}
        </p>
      ) : null}

      <LibraryField
        id="facility-name"
        label="Facility Name"
        required
        error={errors.facilityName}
      >
        <input
          id="facility-name"
          className={libraryInputClass(errors.facilityName)}
          value={form.facilityName}
          onChange={(e) => setField("facilityName", e.target.value)}
          maxLength={255}
          autoFocus
        />
      </LibraryField>

      <LibraryField
        id="facility-type"
        label="Facility Type"
        required
        error={errors.facilityType}
      >
        <div className="relative">
          <select
            id="facility-type"
            className={librarySelectClass(errors.facilityType)}
            value={form.facilityType}
            onChange={(e) => setField("facilityType", e.target.value)}
          >
            {CARE_FACILITY_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
          <ChevronDownIcon
            size={14}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
          />
        </div>
      </LibraryField>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <LibraryField id="facility-code" label="Facility Code">
          <input
            id="facility-code"
            className={libraryInputClass()}
            value={form.facilityCode}
            onChange={(e) => setField("facilityCode", e.target.value)}
            maxLength={50}
          />
        </LibraryField>
        <LibraryField
          id="facility-npi"
          label="NPI Number"
          error={errors.npiNumber}
          hint="Optional — 10 digits when provided"
        >
          <input
            id="facility-npi"
            className={libraryInputClass(errors.npiNumber)}
            value={form.npiNumber}
            onChange={(e) =>
              setField("npiNumber", e.target.value.replace(/\D/g, "").slice(0, 10))
            }
            inputMode="numeric"
            maxLength={10}
          />
        </LibraryField>
      </div>

      <LibraryField id="facility-status" label="Status">
        <div className="relative">
          <select
            id="facility-status"
            className={librarySelectClass()}
            value={form.status}
            onChange={(e) => setField("status", e.target.value)}
          >
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
          <ChevronDownIcon
            size={14}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
          />
        </div>
      </LibraryField>
    </LibraryFormModal>
  );
};

export default CareFacilityFormModal;
